// Toggles the mobile search-form between its compact `trigger` pill and the
// full `revealed` form. When a `scrim` target is present (landing pages /
// /places), opening also unhides the dim/blur backdrop behind the form and
// hides any `hideOnOpen` targets (e.g. the hero photo behind the pill);
// clicking the scrim closes back to the pill. While the scrim is shown,
// `HB.fullscreenOverlay` locks page scroll via its ref-counted `noscroll`
// usage so un-blurred content can't appear underneath while other overlays
// (e.g. the search-form's destination/datepicker panes, popup modals) are
// also mounted on top.
stimulusApp.register(
  "search-form-toggle",
  class extends Stimulus.Controller {
    static targets = ["trigger", "revealed", "scrim", "hideOnOpen"]
    static outlets = ["search--form"]

    #open = false
    #pageOffset = 0
    #paneOpen = false
    #searchFormActive = false
    #submitting = false
    #onPaneOpened = () => {
      this.#paneOpen = true
      this.#setSearchFormActive(false)
    }
    #onPaneClosed = () => {
      this.#paneOpen = false
      // Closing a pane normally re-activates the form. But when the close is
      // part of a submit (e.g. datepicker "Done" auto-submits the changed
      // search, triggering a full page reload), the form is navigating away —
      // re-announcing it active would post `search_form_active` one tick before
      // the reload. The form's `submit` fires *before* this `pane-closed`
      // (Stimulus delivers `activePaneValueChanged` on a microtask, after the
      // synchronous `requestSubmit` in form_controller's `#transitionToState`),
      // so `#submitting` is already set when we get here.
      if (this.#open && !this.#submitting) this.#setSearchFormActive(true)
    }
    #onFormSubmit = () => {
      this.#submitting = true
      // Clear on the next macrotask — after the `pane-closed` microtask above —
      // so a cancelled submit (e.g. failed validation, no navigation) doesn't
      // permanently suppress later re-announcements.
      setTimeout(() => {
        this.#submitting = false
      }, 0)
    }
    #onAppMessage = (event) => {
      let data
      try {
        data = JSON.parse(event.data)
      } catch {
        return
      }
      if (data?.action === "search_placeholder_click" && this.#open) {
        this.close()
      }
    }
    #observeOuterClick = () => {}
    #unobserveOuterClick = () => {}

    // Decides whether a tap should collapse the open form. We deliberately
    // don't `stopPropagation`, so the same gesture still reaches the nav burger
    // / header control and opens it.
    #isOuterClick = (event) => {
      if (event.target.closest(".find-holiday--container")) return false
      // External-placeholder mode: the pill lives outside the container, so
      // without this guard the click that fires `open` would re-close here.
      if (this.hasTriggerTarget && this.triggerTarget.contains(event.target))
        return false
      // popup_modal openers (and an already-mounted modal): our close is sync
      // but the modal mount is async, so closing would drop the shared
      // `noscroll` ref-count to 0 before the modal restores it. Leave the form
      // open behind the modal instead.
      if (
        event.target.closest(
          '[data-turbo-frame="popup_modal"], [data-link-turbo-frame-value="popup_modal"], [data-controller~="popup-modal"]',
        )
      )
        return false
      // The glossary drawer mounts at body level (#root-portal), outside the
      // form container. Tapping its backdrop dismisses the drawer (drawer.js)
      // and must not collapse the search form behind it.
      if (event.target.closest(".drawer, .drawer--overlay")) return false
      return true
    }

    connect() {
      this.#bindListeners("addEventListener")
      // Capture phase is deliberate: header controls like `.dropdown-current`
      // (mobile/dropdowns.js) call `stopPropagation()` on a `body`-delegated
      // handler, which halts the bubble before it reaches `document`. Listening
      // top-down lets us close before that swallows the click. We only observe
      // while the form is open (toggled in `#setOpen`).
      const [observe, unobserve] = HB.useOuterClick(this, {
        triggerEvents: ["click"],
        useCapture: true,
        isOuterEvent: this.#isOuterClick,
        // Collapse without the morph: the morph snapshot renders in the browser
        // top layer, above all normal DOM, so an animated collapse would paint
        // the fading form over the header control this same tap just opened (no
        // z-index can reorder a top-layer snapshot).
        onClose: () => this.#setOpen(false, false),
      })
      this.#observeOuterClick = observe
      this.#unobserveOuterClick = unobserve
      unobserve()
    }

    searchFormOutletConnected(outlet) {
      // Hash-driven entry (e.g. `/places#destinations`) lets `search--form`
      // open a pane during its own connect(), which runs before this sibling
      // controller binds its `hb:search-form:pane-opened` listener — so the
      // event is missed. Mirror the outlet's pane state so the native Back
      // override targets the open sub-pane, not the whole form.
      this.#paneOpen = Boolean(outlet.activePaneValue)
    }

    open() {
      this.#setOpen(true)
    }

    close() {
      this.#setOpen(false)
    }

    disconnect() {
      this.#bindListeners("removeEventListener")
      // Turbo navigation / morph / bfcache can tear the controller down
      // while open. Route through `#setOpen(false)` so the DOM, scroll
      // position, and ref-counted overlay lock all unwind together —
      // otherwise reused nodes come back as "open UI, closed state" where
      // `close()` no-ops (`#open` starts `false` after reconnect) and the
      // pill stays hidden behind the expanded form.
      if (this.#open) {
        this.#setOpen(false)
      }
    }

    #setOpen(open, animate = true) {
      // Guard against double calls so the ref-counted overlay usage below
      // stays balanced — Stimulus actions on `.hidden` (`display: none`)
      // elements normally can't fire twice, but be defensive in case a
      // future call site invokes `open`/`close` programmatically.
      if (open === this.#open) return

      // On a real collapse, drop the user's unsubmitted pane edits so re-opening
      // shows the applied search, not changes they never searched for. Skip while
      // `#submitting`: that close is part of a submit navigating away with those
      // very values, so reverting them would fight the in-flight search.
      if (!open && !this.#submitting && this.hasSearchFormOutlet) {
        this.searchFormOutlet.revertUnsavedChanges()
      }

      const apply = () => {
        this.#open = open

        // Hash-driven entry (e.g. `/places#destinations`) opens a sub-pane
        // before the placeholder is tapped, so the form may be expanding with a
        // pane already mounted. In that case the native Back should target the
        // pane first; only flag the form itself active when no pane is open.
        this.#setSearchFormActive(open && !this.#paneOpen)

        this.triggerTarget.classList.toggle("hidden", open)
        this.revealedTarget.classList.toggle("hidden", !open)
        this.hideOnOpenTargets.forEach((el) =>
          el.classList.toggle("hidden", open),
        )
        if (this.hasScrimTarget) {
          this.scrimTarget.classList.toggle("hidden", !open)
          // Use the shared ref-counted `noscroll` API so we cooperate with
          // search panes / modals that may mount on top of the search form.
          // Toggling `body.noscroll` directly here would let a nested modal's
          // unmount clear our lock (or our close clear the modal's lock).
          if (open) {
            window.HB.fullscreenOverlay.beforeMount()
            this.#pageOffset = window.pageYOffset
          } else {
            window.HB.fullscreenOverlay.beforeUnmount()
            // `fullscreenOverlay` flips body to `position: fixed` while open;
            // restore the scroll position the page had before opening so the
            // view doesn't snap to the top once the lock is released.
            window.scrollTo(0, this.#pageOffset)
          }
        }

        if (open) this.#observeOuterClick()
        else this.#unobserveOuterClick()
      }

      // Wrap the state change in a View Transition so the placeholder pill
      // and the open form (sharing `view-transition-name: search-form-morph`)
      // are tweened as one morphing element. Browsers without the API, and
      // users requesting reduced motion, get the direct state change.
      //
      // `view-transition-name` permanently creates a stacking context (and
      // acts like `contain: layout`) on the named element, which breaks
      // unrelated layouts: the placeholder pill becomes non-interactable in
      // Chrome 141+, and the open form traps the fullscreen pane overlay
      // inside its own stacking context. So we scope the name to the actual
      // morph window via a CSS class — descendants opt in via
      // `.search-form-toggle_morphing .search-form-placeholder/.place-search
      // { view-transition-name: search-form-morph }`.
      const reduceMotion = window.matchMedia?.(
        "(prefers-reduced-motion: reduce)",
      )?.matches

      if (
        animate &&
        !reduceMotion &&
        typeof document.startViewTransition === "function"
      ) {
        // Drop the form's z-index for the collapse morph. The transition
        // captures the "old" snapshot now — before `apply` hides the scrim and
        // the `:has` boost falls away — so without this the collapsing form
        // keeps its boosted z-index and paints over the page for the length of
        // the transition.
        if (!open) document.body.classList.add("search-form-collapsing")
        this.element.classList.add("search-form-toggle_morphing")
        const transition = document.startViewTransition(apply)
        transition.finished.finally(() => {
          this.element.classList.remove("search-form-toggle_morphing")
          document.body.classList.remove("search-form-collapsing")
        })
      } else {
        apply()
      }
    }

    #setSearchFormActive(active) {
      if (active === this.#searchFormActive) return
      this.#searchFormActive = active
      const action = active ? "search_form_active" : "search_form_active_stop"
      HB.postMessageForVersion({ action }, "2.5.5")
    }

    #bindListeners(method) {
      window[method]("message", this.#onAppMessage)
      this.element[method]("hb:search-form:pane-opened", this.#onPaneOpened)
      this.element[method]("hb:search-form:pane-closed", this.#onPaneClosed)
      this.element[method]("submit", this.#onFormSubmit)
    }
  },
)
